import React, { useEffect, useState } from "react";
import AdminUserModal from "./AdminUserModal";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";



function Card({ title, value, subtitle, color = "#2563eb" }) {
  return (
    <div style={{
      background: "white", padding: "20px", borderRadius: "12px",
      border: "1px solid #e5e7eb",
    }}>
      <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "6px" }}>{title}</div>
      <div style={{ fontSize: "28px", fontWeight: 700, color }}>{value}</div>
      {subtitle && <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>{subtitle}</div>}
    </div>
  );
}

function fmtBytes(bytes) {
  if (!bytes) return "—";
  const gb = bytes / 1024 / 1024 / 1024;
  if (gb >= 1) return gb.toFixed(2) + " GB";
  return (bytes / 1024 / 1024).toFixed(0) + " MB";
}

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [system, setSystem] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [openedUserId, setOpenedUserId] = useState(null);

  const load = async () => {
    try {
      const [sRes, sysRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/stats`, { credentials: "include" }),
        fetch(`${API_BASE}/api/admin/system`, { credentials: "include" }),
      ]);
      if (sRes.status === 403 || sysRes.status === 403) {
        setError("Доступ только для администратора");
        return;
      }
      if (!sRes.ok || !sysRes.ok) {
        setError("Ошибка загрузки");
        return;
      }
      setStats(await sRes.json());
      setSystem(await sysRes.json());
      setError(null);
      setLastUpdate(new Date().toLocaleTimeString('ru-RU'));
    } catch (err) {
      setError("Ошибка сети: " + err.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);   // автообновление каждые 10 сек
    return () => clearInterval(id);
  }, []);

  if (error) return <div style={{ padding: 40, color: "red" }}>{error}</div>;
  if (!stats || !system) return <div style={{ padding: 40 }}>Загрузка...</div>;

  const { users, campaigns, emails, queue, online, top_users, recent_users, payments_24h } = stats;

  return (
    <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h1 style={{ fontSize: "26px", fontWeight: 700 }}>🔧 Админ-панель</h1>
        <div style={{ fontSize: "13px", color: "#6b7280" }}>
          Обновлено: {lastUpdate} · автообновление каждые 10 сек
        </div>
      </div>

      {/* Пользователи */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "20px" }}>👥 Пользователи</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
        <Card title="Всего" value={users.total} />
        <Card title="Платных" value={users.paid} color="#16a34a" />
        <Card title="Новых за 24ч" value={users.new_24h} color="#3b82f6" />
        <Card title="Новых за 7 дней" value={users.new_7d} />
        <Card title="Онлайн (15 мин)" value={online} color="#8b5cf6" subtitle="уникальных активных" />
      </div>

      {/* Активность */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "24px" }}>📧 Активность</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
        <Card title="Отправлено 24ч" value={emails.sent_24h} color="#16a34a" />
        <Card title="Ошибок 24ч" value={emails.failed_24h} color="#ef4444" />
        <Card title="Кампаний running" value={campaigns.running} color="#3b82f6" />
        <Card title="Кампаний paused" value={campaigns.paused} color="#f59e0b" />
        <Card title="Всего кампаний" value={campaigns.total} />
      </div>

      {/* Платежи и очередь */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "24px" }}>💰 Оплаты и очередь</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
        <Card title="Оплат 24ч" value={payments_24h.count} color="#16a34a" />
        <Card title="Сумма 24ч" value={payments_24h.total.toLocaleString('ru-RU') + " ₽"} color="#16a34a" />
        <Card title="Очередь waiting" value={queue.waiting} color={queue.waiting > 100 ? "#f59e0b" : "#6b7280"} />
        <Card title="Очередь active" value={queue.active} color="#3b82f6" />
        <Card title="Очередь failed" value={queue.failed} color={queue.failed > 0 ? "#ef4444" : "#6b7280"} />
      </div>

      {/* Система */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "24px" }}>🖥️ Система</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
        <Card title="CPU Load (1m)" value={system.cpu.load_1m} subtitle={`${system.cpu.cores} ядер`}
              color={parseFloat(system.cpu.load_1m) > system.cpu.cores ? "#ef4444" : "#16a34a"} />
        <Card title="RAM" value={`${system.memory.percent}%`}
              subtitle={`${fmtBytes(system.memory.used)} / ${fmtBytes(system.memory.total)}`}
              color={parseFloat(system.memory.percent) > 85 ? "#ef4444" : "#16a34a"} />
        <Card title="Диск" value={system.disk?.percent || "—"}
              subtitle={`${fmtBytes(system.disk?.used)} / ${fmtBytes(system.disk?.total)}`}
              color={system.disk && parseFloat(system.disk.percent) > 85 ? "#ef4444" : "#16a34a"} />
        <Card title="Uptime сервера" value={`${Math.floor(system.uptime_sec / 3600)} ч`} />
      </div>

      {/* PM2 процессы */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "24px" }}>⚙️ Процессы</h2>
      <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={{ padding: "10px", textAlign: "left" }}>Имя</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Статус</th>
              <th style={{ padding: "10px", textAlign: "right" }}>CPU</th>
              <th style={{ padding: "10px", textAlign: "right" }}>RAM</th>
              <th style={{ padding: "10px", textAlign: "right" }}>Рестарты</th>
            </tr>
          </thead>
          <tbody>
            {system.pm2.map(p => (
              <tr key={p.name} style={{ borderTop: "1px solid #f3f4f6" }}>
                <td style={{ padding: "10px" }}>{p.name}</td>
                <td style={{ padding: "10px", color: p.status === 'online' ? "#16a34a" : "#ef4444" }}>{p.status}</td>
                <td style={{ padding: "10px", textAlign: "right" }}>{p.cpu}%</td>
                <td style={{ padding: "10px", textAlign: "right" }}>{fmtBytes(p.memory)}</td>
                <td style={{ padding: "10px", textAlign: "right", color: p.restarts > 10 ? "#ef4444" : "#6b7280" }}>
                  {p.restarts}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Топ юзеров */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "24px" }}>🏆 Топ юзеров (24ч)</h2>
      <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={{ padding: "10px", textAlign: "left" }}>Email</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Тариф</th>
              <th style={{ padding: "10px", textAlign: "right" }}>Отправлено</th>
            </tr>
          </thead>
          <tbody>
            {top_users.map(u => (
              <tr key={u.id} style={{ borderTop: "1px solid #f3f4f6" }}>
<td style={{ padding: "10px" }}>
  <button
    onClick={() => setOpenedUserId(u.id)}
    style={{
      background: "transparent",
      border: "none",
      color: "#2563eb",
      cursor: "pointer",
      padding: 0,
      fontSize: "14px",
      textDecoration: "underline",
    }}
    title="Открыть карточку"
  >
    {u.email}
  </button>
</td>
                <td style={{ padding: "10px" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: "6px", fontSize: "12px",
                    background: u.plan === 'free' ? "#f3f4f6" : "#dcfce7",
                    color: u.plan === 'free' ? "#6b7280" : "#166534",
                  }}>{u.plan}</span>
                </td>
                <td style={{ padding: "10px", textAlign: "right", fontWeight: 600 }}>{u.sent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Последние регистрации */}
      <h2 style={{ fontSize: "16px", color: "#6b7280", marginBottom: "12px", marginTop: "24px" }}>🆕 Последние регистрации</h2>
      <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              <th style={{ padding: "10px", textAlign: "left" }}>Email</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Имя</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Тариф</th>
              <th style={{ padding: "10px", textAlign: "right" }}>Дата</th>
            </tr>
          </thead>
          <tbody>
            {recent_users.map(u => (
              <tr key={u.id} style={{ borderTop: "1px solid #f3f4f6" }}>
<td style={{ padding: "10px" }}>
  <button
    onClick={() => setOpenedUserId(u.id)}
    style={{
      background: "transparent",
      border: "none",
      color: "#2563eb",
      cursor: "pointer",
      padding: 0,
      fontSize: "14px",
      textDecoration: "underline",
    }}
    title="Открыть карточку"
  >
    {u.email}
  </button>
</td>
                <td style={{ padding: "10px", color: "#6b7280" }}>{u.name || "—"}</td>
                <td style={{ padding: "10px" }}>{u.plan}</td>
                <td style={{ padding: "10px", textAlign: "right", color: "#6b7280" }}>
                  {new Date(u.created_at).toLocaleString('ru-RU')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openedUserId && (
  <AdminUserModal
    userId={openedUserId}
    onClose={() => setOpenedUserId(null)}
  />
)}
    </div>
  );
}